/*
 * This button is used for the Follow / Unfollow user functionality in the profile:
 *  * When viewing someone else's profile
 *  * when viewing your own follower / following list
 *  * When viewing a friends list from a social integration
 * */

import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { TitleCasePipe } from '@angular/common';

// services
import { TranslateService } from '@ngx-translate/core';
import { ButtonType } from '@app/shared/temporary-types';

@Component({
  selector: 'dgx-follow-user-button',
  templateUrl: './follow-user-button.component.html',
  styleUrls: ['./follow-user-button.component.scss'],
  providers: [TitleCasePipe],
})
export class FollowUserButton implements OnInit, OnChanges {
  @Input() public isActive: boolean = false;
  @Input() public altBg: string = '';
  @Input() public hasPromise: boolean = false;
  @Input() public a11yContext: string = '';
  @Input() public submitting: boolean = false;
  @Input() public buttonClasses: string;
  @Input() public showFollowingLabel: boolean;

  @Output() public onToggle: EventEmitter<{
    isActive: boolean;
    event: Event;
  }> = new EventEmitter();

  public buttonType: ButtonType = 'primary';
  public buttonLabel = '';
  public icon = '';
  public ariaPressed = false;
  public isHover = false;

  public i18n = this.translateService.instant([
    'Core_Follow',
    'Core_Following',
    'Core_UnFollow',
  ]);

  public icons = {
    default: 'plus',
    active: 'checkmark',
    undo: 'cross',
  };

  @ViewChild('button') private buttonElement: ElementRef<HTMLElement>;

  constructor(
    private translateService: TranslateService,
    private titleCasePipe: TitleCasePipe
  ) {}

  public ngOnInit(): void {
    this.setStates();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isActive || changes.submitting) {
      this.setStates();
    }
  }

  public onFocus(): void {
    this.isHover = true;
    this.buttonLabel = this.getLabel();
    this.icon = this.getIcon();
    this.buttonType = this.getButtonType();
  }

  public onBlur(): void {
    this.isHover = false;
    this.buttonLabel = this.getLabel();
    this.icon = this.getIcon();
    this.buttonType = this.getButtonType();
  }

  public onClick(data: { isActive: boolean; event: Event }): void {
    if (this.hasPromise) {
      // emit the passed in data and wait for the 'followStatusChanged' event to be received before updating
      // the button states
      this.onToggle.emit(data);
    } else {
      // emit the passed in data, but don't wait for a response before updating states
      this.isActive = !data.isActive;
      this.onToggle.emit(data);
      this.isHover = false;
      this.buttonLabel = this.getLabel();
      this.icon = this.getIcon();
    }
  }

  private setStates(): void {
    this.buttonLabel = this.getLabel();
    this.buttonType = this.getButtonType();
    this.icon = this.getIcon();
    this.ariaPressed = this.isActive;
  }

  private getLabel(): string {
    if (!this.showFollowingLabel && this.isActive) {
      return '';
    }
    let buttonLabel = '';
    if (this.isActive) {
      if (this.isHover) {
        buttonLabel = this.i18n.Core_UnFollow;
      } else {
        buttonLabel = this.i18n.Core_Following;
      }
    } else {
      buttonLabel = this.i18n.Core_Follow;
    }
    return this.titleCasePipe.transform(buttonLabel);
  }

  private getButtonType(): ButtonType {
    if (this.isActive && !this.altBg) {
      return 'primary';
    } else {
      return 'passive';
    }
  }
  private getIcon(): string {
    let icon;
    if (this.isActive) {
      if (this.isHover) {
        icon = this.icons.undo;
      } else {
        icon = this.icons.active;
      }
    } else {
      icon = this.icons.default;
    }
    return icon;
  }
}
